import React, { useCallback, useEffect, useRef } from 'react';
import { Box3, Vector3 } from 'three';

import { convertDegreesToRadians } from '@paragon/geometry-library';
import { Point2D } from '@paragon/three-shared';

import { BoundingHitbox } from '../util/BoundingHitBox';
import { threeJsDom } from '../util/events/ThreeJSDom';
import { GlobalThreeJSDomObjectEvent } from '../util/events/types';
import { R3FSvgVisual } from '../util/R3FSvgVisual';

interface Props {
  radius: number;
  size: number;
  angle: number;
  anchorPoint: Point2D;
  onStartMoveCallBack: () => void;
  onEndMoveCallBack: () => void;
  onRotateCallBack: (angle: number) => void;
}

export function RotateHandle({
  radius,
  size,
  angle,
  anchorPoint,
  onStartMoveCallBack,
  onEndMoveCallBack,
  onRotateCallBack,
}: Props) {
  const theta = convertDegreesToRadians(angle);

  const position = new Vector3()
    .setX(radius * Math.sin(-theta) + anchorPoint.x)
    .setY(radius * Math.cos(theta) + anchorPoint.y);

  const rotating = useRef(false);

  const handleMouseDown = useCallback(() => {
    rotating.current = true;
    onStartMoveCallBack();
  }, [onStartMoveCallBack]);

  const stopRotation = useCallback(() => {
    if (rotating.current) onEndMoveCallBack();
    rotating.current = false;
  }, [onEndMoveCallBack]);

  useEffect(() => {
    threeJsDom.addGlobalListener('mouseleave', stopRotation);
    threeJsDom.addGlobalListener('mouseup', stopRotation);
    return () => {
      threeJsDom.removeGlobalListener('mouseleave', stopRotation);
      threeJsDom.removeGlobalListener('mouseup', stopRotation);
    };
  }, [stopRotation]);

  const handleMouseMove = useCallback(
    (event: GlobalThreeJSDomObjectEvent) => {
      if (!rotating.current) return;
      const mousePosition = event.eventPosition.worldPosition;
      // The rotate handle considers zero angle to represent upwards
      const angle =
        (2.5 * Math.PI - Math.atan2(mousePosition.y - anchorPoint.y, anchorPoint.x - mousePosition.x)) % (2 * Math.PI);
      onRotateCallBack(angle);
    },
    [onRotateCallBack, anchorPoint],
  );

  useEffect(() => {
    threeJsDom.addGlobalListener('mousemove', handleMouseMove);
    return () => threeJsDom.removeGlobalListener('mousemove', handleMouseMove);
  }, [handleMouseMove]);

  const halfSize = size / 2;
  const box = new Box3(new Vector3(-halfSize, -halfSize), new Vector3(halfSize, halfSize, 1));

  return (
    <group position={position}>
      <BoundingHitbox box3={box} mouseDown={handleMouseDown} />
      <R3FSvgVisual iconName="icon-rotate-fa" rotation={0} size={size} />
    </group>
  );
}
